import type { MapFilterData } from '@propileya/geo-widget';
import {
  Category,
  Equipment,
  OfferType,
  OfferTypeObject,
  Unit,
  UnitsMultilevelList,
} from '@/types';
import { filtersCountUpdater, getUnitList } from '@/common/helpers';

export const filterValidCodes = <T extends { code?: string | null; title?: string | null }>(
  items: T[],
): (T & { title: string })[] =>
  items
    .filter((i): i is T & { code: string } => !!i.code && i.code.toLowerCase() !== 'null')
    .map((i) => ({ ...i, title: i.title || i.code }));

export type MenuState = {
  offerTypeList: OfferTypeObject[];
  categoriesMenuList: Category[];
  equipmentMenuList: Equipment[];
  unitList: UnitsMultilevelList | undefined;
  rangesDimensions: { roomNumber: number[]; price: number[]; rentPrice: number[]; area: number[] };
  powerConsumptionClassesList: Category[];
  energyClassFactorsList: Category[];
};

export const applyCountersToMenu = (
  prev: MenuState,
  filterData: MapFilterData | null | undefined,
): MenuState => {
  if (filterData === undefined) {
    return {
      ...prev,
      offerTypeList: prev.offerTypeList.map((i) => ({ ...i, count: 0 })),
      categoriesMenuList: prev.categoriesMenuList.map((i) => ({ ...i, count: 0 })),
      equipmentMenuList: prev.equipmentMenuList.map((i) => ({ ...i, count: 0 })),
      unitList: prev.unitList?.map((i) => ({ ...i, count: 0 })),
      powerConsumptionClassesList: prev.powerConsumptionClassesList.map((i) => ({
        ...i,
        count: 0,
      })),
      energyClassFactorsList: prev.energyClassFactorsList.map((i) => ({ ...i, count: 0 })),
    };
  }

  if (!filterData) return prev;

  const offerTypeList =
    filterData.offerTypRentTotalCount != null && filterData.offerTypSellTotalCount != null
      ? prev.offerTypeList.map((item) => {
          if (item.code === OfferType.RENT)
            return { ...item, count: filterData.offerTypRentTotalCount! };
          if (item.code === OfferType.SELL)
            return { ...item, count: filterData.offerTypSellTotalCount! };
          return item;
        })
      : prev.offerTypeList;

  const normalizeOption = (
    items: Array<{ code: string; title: string | null; count: number }>,
  ): Category[] => items.map((i) => ({ ...i, title: i.title ?? i.code }));

  const normalizeUnit = (
    items: Array<{
      code: string;
      title: string | null;
      count: number;
      admunitIds?: string[];
      children?: unknown[];
    }>,
  ): Unit[] =>
    items.map((i) => ({
      ...i,
      title: i.title ?? i.code,
      children: (i.children as Unit[] | undefined) ?? [],
    }));

  const categoriesMenuList = filterData.categories
    ? filtersCountUpdater(
        prev.categoriesMenuList,
        normalizeOption(
          filterData.categories as Array<{ code: string; title: string | null; count: number }>,
        ),
      )
    : prev.categoriesMenuList;

  const equipmentMenuList = filterData.equipment
    ? filtersCountUpdater(
        prev.equipmentMenuList,
        normalizeOption(
          filterData.equipment as Array<{ code: string; title: string | null; count: number }>,
        ),
      )
    : prev.equipmentMenuList;

  let unitList = prev.unitList;
  if (filterData.countries && filterData.federalStates && filterData.districts) {
    const rawCountries = filterData.countries as Array<{
      code: string;
      title: string | null;
      count: number;
      admunitIds?: string[];
      children?: unknown[];
    }>;
    const rawFederalStates = filterData.federalStates as typeof rawCountries;
    const rawDistricts = filterData.districts as typeof rawCountries;
    const currentList = getUnitList(
      normalizeUnit(rawCountries),
      normalizeUnit(rawFederalStates),
      normalizeUnit(rawDistricts),
    );
    unitList = filtersCountUpdater(prev.unitList ?? [], currentList);
  }

  const powerConsumptionClassesList = filterData.powerConsumptionClasses
    ? filtersCountUpdater(
        prev.powerConsumptionClassesList,
        normalizeOption(
          filterData.powerConsumptionClasses as Array<{
            code: string;
            title: string | null;
            count: number;
          }>,
        ),
      )
    : prev.powerConsumptionClassesList;

  const energyClassFactorsList = filterData.energyClassFactors
    ? filtersCountUpdater(
        prev.energyClassFactorsList,
        normalizeOption(
          filterData.energyClassFactors as Array<{
            code: string;
            title: string | null;
            count: number;
          }>,
        ),
      )
    : prev.energyClassFactorsList;

  return {
    ...prev,
    offerTypeList,
    categoriesMenuList,
    equipmentMenuList,
    unitList,
    powerConsumptionClassesList,
    energyClassFactorsList,
  };
};
